import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';

import Section from '../Section';
import Image from 'vibo-ui/Image';
import Textarea from 'vibo-ui/Textarea';
import SectionTime from '../SectionTime';
import SectionTools from './SectionTools';
import ProLabel from 'components/labels/ProLabel';
import SectionBlocksInfo from './SectionBlocksInfo';
import AddSectionInMiddle from './AddSectionInMiddle';
import WithTimelineSection from '../WithTimelineSection';
import VisibilityLabel from 'components/labels/VisibilityLabel';
import TimelineSectionSongsInfo from './TimelineSectionSongsInfo';
import StopPropagationWrapper from '../../common/StopPropagationWrapper';
import { EventContext } from '../../context/EventContext';
import { TimelineSectionContext } from './TimelineSectionContext';

import { MAX_TITLE_LENGTH } from 'services/constants';
import { strippString } from 'services/common/stringHelpers';
import { useUpdateSectionDebounce } from 'graphql/hooks/sections';
import {
  isHeadlineSection,
  isDontPlaySection,
  getTimelineSectionProgress,
} from 'services/sections/helpers';

import { TimelineSectionProps } from './interfaces';
import { SectionType, SectionVisibilityRoles } from 'types/enums';

import useStyles from './style';

const TimelineSection = <TSection extends TimelineSectionBase>({
  index,
  section,
  isSelected,
  className,
  onClick,
  onDeleteClick,
  onEditClick,
  ...rest
}: TimelineSectionProps<TSection>): React.ReactElement => {
  const classes = useStyles();

  const {
    isEventDj,
    isEventGuest,
    isEventHost,
    isTemplate,
    isFavoriteSections,
    canEdit,
    showSectionInfo = false,
    canAddSection = false,
  } = useContext(EventContext);

  const isHeadline = useMemo(() => isHeadlineSection(section), [section.type, section._id]);
  const isDontPlay = useMemo(() => isDontPlaySection(section), [section.type, section._id]);
  // @ts-ignore
  const isPro = section.isPro;

  const isNameDisabledForHost = useMemo(
    () => isEventHost && !section.settings.canHostChangeSectionName,
    [isEventHost, section.settings.canHostChangeSectionName]
  );
  const isTimeHiddenForHost = useMemo(
    () => isEventHost && !section.settings.canHostChangeSectionTime,
    [isEventDj, section.settings.canHostChangeSectionTime]
  );

  const { handleUpdateSection } = useUpdateSectionDebounce<EventSection>();

  const hasTimeRow: boolean = useMemo(
    () =>
      !isDontPlay &&
      section.settings.timeEnabled &&
      (canEdit || !!section.time) &&
      !isFavoriteSections,
    [isDontPlay, canEdit, section.settings.timeEnabled, section.time]
  );

  const notesContent = useMemo(() => !!strippString(section.note), [section.note]);

  const showSongsInfo = useMemo(() => section.settings.songsEnabled, [
    section.settings.songsEnabled,
  ]);
  const showQuestionsInfo = useMemo(() => section.settings.questionsEnabled && showSectionInfo, [
    section.settings.questionsEnabled,
    showSectionInfo,
  ]);
  const showNotesInfo = useMemo(
    () =>
      section.settings.notesEnabled &&
      notesContent &&
      (isEventDj || (isEventHost && !!section.settings.notesVisibleForHosts)),
    [
      section.settings.notesEnabled,
      notesContent,
      isEventDj,
      isEventHost,
      section.settings.notesVisibleForHosts,
    ]
  );
  const showVisibilityInfo = useMemo(
    () => section.visibility !== SectionVisibilityRoles.host && !isEventGuest,
    [section.visibility, isEventGuest]
  );
  const withImage = section.sectionImage?.imageUrl;

  const proress =
    isTemplate || isFavoriteSections || isEventGuest
      ? undefined
      : getTimelineSectionProgress(section);

  return (
    <TimelineSectionContext.Provider
      value={{
        section,
        isDontPlay: false,
        isHeadLine: section.type === SectionType.headline,
      }}
    >
      <div
        className={classNames('timelineSection', classes.timelineSection, className, {
          headline: isHeadline,
          withImage: withImage,
        })}
        id={section._id}
        data-index={index}
        {...rest}
      >
        {canAddSection && !isFavoriteSections ? (
          <AddSectionInMiddle sectionId={section._id} />
        ) : null}
        <Section
          content={{
            top: (
              <>
                <Textarea
                  className={classNames('editableSectionName', classes.editableSectionName, {
                    isDontPlay,
                  })}
                  onChange={(_, value) => {
                    !!value.trim().length &&
                      handleUpdateSection?.({
                        sectionId: section._id,
                        payload: { name: value },
                      });
                  }}
                  value={section.name}
                  maxLength={MAX_TITLE_LENGTH}
                  disabled={isEventGuest || isDontPlay || isNameDisabledForHost || !canEdit}
                  data-gtm-target-id="timeline-edit-section-name"
                />
                <div className="sectionExtra">
                  {hasTimeRow ? (
                    <StopPropagationWrapper>
                      <SectionTime
                        time={section.time}
                        onTimeChange={time =>
                          handleUpdateSection?.({
                            sectionId: section._id,
                            payload: { time },
                          })
                        }
                        disabled={isTimeHiddenForHost || !canEdit}
                      />
                    </StopPropagationWrapper>
                  ) : null}
                  {canEdit ? (
                    <SectionTools
                      section={section}
                      // @ts-ignore
                      onEditClick={onEditClick}
                      // @ts-ignore
                      onDeleteClick={onDeleteClick}
                    />
                  ) : null}
                </div>
              </>
            ),
            middle:
              section.songsCount && section.settings.songsEnabled && section.songsCount === 1 ? (
                <WithTimelineSection exludedTypes={[SectionType.headline]} section={section}>
                  <TimelineSectionSongsInfo section={section} />
                </WithTimelineSection>
              ) : null,
            bottom: (
              <WithTimelineSection exludedTypes={[SectionType.headline]} section={section}>
                <>
                  <SectionBlocksInfo
                    progress={proress}
                    showNotesInfo={showNotesInfo}
                    showSongsInfo={showSongsInfo}
                    showQuestionsInfo={showQuestionsInfo}
                  />
                  <div className="sectionLabels">
                    {isPro ? <ProLabel size="lg" /> : null}
                    {showVisibilityInfo ? (
                      <VisibilityLabel type={section.visibility} showTooltip={false} />
                    ) : null}
                  </div>
                </>
              </WithTimelineSection>
            ),
          }}
          isSelected={isSelected}
          onClick={() => !isHeadline && onClick(section)}
          classes={{
            root: classNames({
              headline: isHeadline,
              dontPlay: isDontPlay,
            }),
            middle: classes.sectionMiddle,
          }}
        />
      </div>
      {isHeadline && !!section.sectionImage?.imageUrl ? (
        <Image
          src={section.sectionImage?.imageUrl}
          className={classNames(classes.sectionImage, 'sectionImage')}
        />
      ) : null}
    </TimelineSectionContext.Provider>
  );
};

export default TimelineSection;
